import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import RowCandidateCard from '../LeaderboardProfiles/RowCandidateCard';
import { useEffect, useState } from 'react';
import RecruiterProfileMenu from './RecruiterProfileMenu';
import { getRecruiterWishlist } from '../../../redux/actions/halloffame';
import { Mixpanel } from '../../../config/Mixpanel';
import Constants from '../../../common/constants';
import RecruiterDetails from './RecruiterDetails';
import RecruiterAuth from '../RecruiterAuth';

const RecruiterProfile = () => {

  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(true);
  const [wishlistedProfiles, setWishlistedProfiles] = useState([]);
  const [isRecruiterDetailsOpen, setIsRecruiterDetailsOpen] = useState(false);
  const [isRecruiterAuthOpen, setIsRecruiterAuthOpen] = useState(false);

  const recruiterAccessToken = useSelector((store) => store?.hallOfFame?.recruiterAccessToken);
  const recruiterData = useSelector((store) => store?.hallOfFame?.recruiterData);
  const navigate = useNavigate();
  const recruiterAuthData = useSelector((state) => state.hallOfFame.recruiterAuthData);

  useEffect(() => {
    if (recruiterAuthData) {
      if (recruiterAuthData.onboarding_required) {
        setIsRecruiterDetailsOpen(true);
      }
    }
  }, [recruiterAuthData]);

  useEffect(() => {
    Mixpanel.track(Constants.Mixpanel.RECRUITER_SHORTLIST_PAGE_LOADED);
    if (!recruiterAccessToken) {
      setIsLoading(false);
      setIsRecruiterAuthOpen(true);
      return;
    }
    setIsLoading(true);
    dispatch(getRecruiterWishlist(recruiterAccessToken, (response) => {
      setWishlistedProfiles(response?.candidates);
      setIsLoading(false);
    }, (error) => {
      setIsLoading(false);
      if (error?.response?.status === 401) {
        setIsRecruiterAuthOpen(true);
        return;
      }
      console.log(error);
    }));
  }, [dispatch, recruiterAccessToken]);

  return (
    <div className='min-h-screen p-4 font-sans text-white flex flex-col items-center gap-8 md:gap-12'
      style={{
        background: "radial-gradient(55.67% 55.67% at 50% 44.33%, #002842 0%, #001C2C 100%)"
      }}>
      <div className="mb-4 flex w-full items-center justify-between">
        {/* Logo Section */}
        <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate("/recruiter")}>
          <div className="flex w-fit items-center gap-2 rounded-md bg-white/10 p-2">
            <img
              src="https://assets.learntube.ai/files/new%20Linkedin%20data/image%2011.png?updatedAt=1747309992449"
              alt="LearnTube Logo"
              width={24}
              height={24}
              className="h-6 w-6"
            />
            <p className="text-xs font-medium text-white">LearnTube.ai</p>
          </div>
          <div className="h-[36px] w-[2px] bg-white/20" />
          <div className="flex flex-col items-start justify-center">
            <p className="text-[10px] text-white/50">backed by</p>
            <div className="flex items-center gap-1">
              <img
                src="https://assets.learntube.ai/files/post-payment-updates/use2460.svg?updatedAt=1728356838222"
                alt="Google G"
                className="h-3 w-auto"
              />
              <img
                src="https://assets.learntube.ai/files/post-payment-updates/text3075.svg?updatedAt=1728356926579"
                alt="Google for Startups"
                className="h-3 w-auto"
              />
            </div>
          </div>
        </div>

        {/* Profile Icon */}
        <div className="relative">
          <div onClick={() => setIsProfileMenuOpen(true)} className="flex h-10 w-20 items-center justify-center rounded-full bg-[#1A3855] text-white gap-2 cursor-pointer">
            <img
              src={recruiterData?.profile_image || "https://assets.learntube.ai/files/halloffame/recruiter/new-user-icon.svg"}
              alt="User"
              className="h-5 w-5 rounded-full"
              referrerPolicy="no-referrer"
              onError={(e) => {
                e.target.onerror = null; // Prevent infinite loop
                e.target.src = "https://assets.learntube.ai/files/halloffame/recruiter/new-user-icon.svg";
              }}
            />
            <img src="https://assets.learntube.ai/files/halloffame/recruiter/breadcrums.svg" alt="breadcrumbs" className="h-5 w-5" />
          </div>
          <RecruiterProfileMenu isOpen={isProfileMenuOpen} onClose={() => setIsProfileMenuOpen(false)} />
        </div>
      </div>
      <div className='mx-auto max-w-7xl w-full h-full flex flex-col items-center justify-center'>
        {isLoading ? (
          <div className="flex flex-col items-center justify-center">
            <p className="text-white text-sm">Loading your shortlist...</p>
          </div>
        ) : (
          <>
            <h2 className='text-2xl font-bold text-center mb-1 text-white'>Your Shortlist</h2>
            <p className='text-sm text-center mb-4 text-[#7896FF]'>{wishlistedProfiles?.length} Professionals listed</p>
            <div className="grid w-full grid-cols-1 gap-3 md:grid-cols-2 md:gap-4">
              {wishlistedProfiles?.map((card, index) => (
                <RowCandidateCard
                  key={card?.id_email || index}
                  rank={card?.match_score}
                  name={card?.profile?.full_name?.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase())}
                  title={card?.experience?.job_title || card?.education?.degree_name}
                  company={card?.experience?.company_name || card?.education?.institution_name}
                  pastTitle={card?.individual_scores?.evidence_breakdown?.experience_evidence?.[0]?.job_title || ''}
                  skillEvidence={card?.individual_scores?.evidence_breakdown?.profile_evidence?.primary_found}
                  isBasicSearch={false}
                  isWishlisted={card?.is_wishlisted}
                  candidateEmail={card?.id_email}
                  profilePicture={card?.profile_photo}
                  onRemove={() => {
                    setWishlistedProfiles((prev) => prev.filter((item) => item?.id_email !== card?.id_email));
                  }}
                  experience={card?.additional_profile_info?.total_experience_months}
                />
              ))}
            </div>
          </>
        )}
      </div>
      <RecruiterDetails
        isOpen={isRecruiterDetailsOpen}
        onClose={() => setIsRecruiterDetailsOpen(false)}
      />
      <RecruiterAuth
        isOpen={isRecruiterAuthOpen}
        onClose={() => setIsRecruiterAuthOpen(false)}
      />
    </div>
  )
}

export default RecruiterProfile